import { useEffect, useRef, useState, type ReactElement } from 'react';
import { Button, InputNumber, Select } from 'antd';
import { useQuery } from '@tanstack/react-query';
import type { ClassBonusRule } from '@salaries/shared';
import { getClassCatalog } from '../../api/classCatalog';
import { DuplicatePanel } from './DuplicatePanel';

// Editor for the per-staff per-class bonus rules: a coach who teaches a specific
// class (by name) at a specific location earns a fixed ₪ supplement per session,
// optionally only on certain days of the week.
//
// The flat list of {class_name, location, days, amount} rules is presented grouped
// by (location, days, amount): every group of classes that share a branch, days
// and an amount is one editable card, so the same set of classes can be added and
// re-priced together instead of one row at a time.

type Group = { id: string; location: string; days: number[]; amount: number; classes: string[] };

const DAY_OPTIONS = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'].map((label, value) => ({
  value,
  label,
}));

const sortDays = (days: number[] | undefined) => [...new Set(days ?? [])].sort((a, b) => a - b);

// Two groups compete for the same meeting when they share a branch and their days
// overlap. "Every day" (no days) only competes with another "every day" group — a
// day-specific group overrides it on its days (the engine picks the day rule).
function daysOverlap(a: number[], b: number[]): boolean {
  if (!a.length || !b.length) return !a.length && !b.length;
  return a.some((d) => b.includes(d));
}

let gidSeq = 0;
const newId = () => `g${gidSeq++}`;

function rulesToGroups(rules: ClassBonusRule[]): Group[] {
  const byKey = new Map<string, Group>();
  for (const r of rules) {
    const days = sortDays(r.days);
    const key = JSON.stringify([r.location, days, r.amount]);
    let g = byKey.get(key);
    if (!g) {
      g = { id: newId(), location: r.location, days, amount: r.amount, classes: [] };
      byKey.set(key, g);
    }
    if (!g.classes.includes(r.class_name)) g.classes.push(r.class_name);
  }
  return [...byKey.values()];
}

function groupsToRules(groups: Group[]): ClassBonusRule[] {
  const seen = new Set<string>(); // class+location+day — keep one rule per slot
  const out: ClassBonusRule[] = [];
  for (const g of groups) {
    if (!g.location || g.amount <= 0) continue; // drop half-filled groups
    const days = sortDays(g.days);
    for (const class_name of g.classes) {
      const keys = (days.length ? days : ['all']).map((d) => JSON.stringify([class_name, g.location, d]));
      if (keys.some((k) => seen.has(k))) continue;
      keys.forEach((k) => seen.add(k));
      out.push({ class_name, location: g.location, amount: g.amount, ...(days.length ? { days } : {}) });
    }
  }
  return out;
}

// Remove the given classes from every competing group (same branch, overlapping
// days) except `exceptId`, so a (class, location, day) slot never lives in two
// groups with different amounts. Drops groups left empty.
function dropClassesFromLocation(
  groups: Group[],
  location: string,
  days: number[],
  classNames: string[],
  exceptId: string,
): Group[] {
  const drop = new Set(classNames);
  return groups
    .map((g) =>
      g.id !== exceptId && g.location === location && daysOverlap(g.days, days)
        ? { ...g, classes: g.classes.filter((c) => !drop.has(c)) }
        : g,
    )
    .filter((g) => g.classes.length > 0);
}

export function ClassBonusFields({
  value,
  onChange,
}: {
  value: ClassBonusRule[];
  onChange: (v: ClassBonusRule[]) => void;
}) {
  const catalogQ = useQuery({ queryKey: ['class-catalog'], queryFn: getClassCatalog });

  // Local group state is the source of truth while editing (keeps stable card
  // identity + input focus across amount edits); re-seed only when `value` is
  // changed from the outside (e.g. server hydration), not by our own emissions.
  const [groups, setGroups] = useState<Group[]>(() => rulesToGroups(value));
  const emitted = useRef<ClassBonusRule[] | null>(null);
  useEffect(() => {
    if (value === emitted.current) return;
    setGroups(rulesToGroups(value));
  }, [value]);

  const commit = (next: Group[]) => {
    setGroups(next);
    const rules = groupsToRules(next);
    emitted.current = rules;
    onChange(rules);
  };

  // Top "add" row: pick many classes + one branch + one amount → merge into the
  // matching (branch, amount) group, or start a new one.
  const [classes, setClasses] = useState<string[]>([]);
  const [loc, setLoc] = useState<string | undefined>();
  const [days, setDays] = useState<number[]>([]);
  const [amt, setAmt] = useState<number>(0);
  const canAdd = classes.length > 0 && !!loc && amt > 0;

  const add = () => {
    if (!canAdd) return;
    const addDays = sortDays(days);
    const target = groups.find(
      (g) => g.location === loc && g.amount === amt && JSON.stringify(g.days) === JSON.stringify(addDays),
    );
    let next: Group[];
    if (target) {
      next = groups.map((g) =>
        g.id === target.id
          ? { ...g, classes: [...g.classes, ...classes.filter((c) => !g.classes.includes(c))] }
          : g,
      );
      next = dropClassesFromLocation(next, loc!, addDays, classes, target.id);
    } else {
      const g: Group = { id: newId(), location: loc!, days: addDays, amount: amt, classes };
      next = dropClassesFromLocation([...groups, g], loc!, addDays, classes, g.id);
    }
    commit(next);
    setClasses([]);
    setLoc(undefined);
    setDays([]);
    setAmt(0);
  };

  // Moving a card to another branch/days takes its classes with it: any other card
  // now competing for the same slots loses them (this card's edit wins).
  const patchGroup = (id: string, patch: Partial<Pick<Group, 'location' | 'days' | 'amount'>>) => {
    const target = groups.find((g) => g.id === id);
    if (!target) return;
    const updated = { ...target, ...patch, days: sortDays(patch.days ?? target.days) };
    let next = groups.map((g) => (g.id === id ? updated : g));
    if (patch.location !== undefined || patch.days !== undefined) {
      next = dropClassesFromLocation(next, updated.location, updated.days, updated.classes, id);
    }
    commit(next);
  };

  const setGroupClasses = (id: string, cls: string[]) => {
    const target = groups.find((g) => g.id === id);
    if (!target) return;
    const added = cls.filter((c) => !target.classes.includes(c));
    let next = groups.map((g) => (g.id === id ? { ...g, classes: cls } : g));
    next = dropClassesFromLocation(next, target.location, target.days, added, id);
    commit(next.filter((g) => g.classes.length > 0));
  };

  const removeGroup = (id: string) => commit(groups.filter((g) => g.id !== id));

  // שכפול: copy a card (classes + days + amount) to more branches. At each branch
  // it merges like "הוסף" does — a class another card already pays there on the
  // same days moves to the copy's amount.
  const [dup, setDup] = useState<{ id: string; locs: string[] } | null>(null);
  const planDup = () => {
    const src = groups.find((g) => g.id === dup?.id);
    if (!dup || !src) return { next: groups, added: 0, replaced: 0 };
    let next = groups;
    let added = 0;
    let replaced = 0;
    for (const location of dup.locs) {
      if (location === src.location) continue;
      for (const c of src.classes) {
        const has = next.some((g) => g.location === location && daysOverlap(g.days, src.days) && g.classes.includes(c));
        if (has) replaced++;
        else added++;
      }
      const target = next.find(
        (g) => g.location === location && g.amount === src.amount && JSON.stringify(g.days) === JSON.stringify(src.days),
      );
      if (target) {
        next = next.map((g) =>
          g.id === target.id ? { ...g, classes: [...g.classes, ...src.classes.filter((c) => !g.classes.includes(c))] } : g,
        );
        next = dropClassesFromLocation(next, location, src.days, src.classes, target.id);
      } else {
        const g: Group = { id: newId(), location, days: src.days, amount: src.amount, classes: [...src.classes] };
        next = dropClassesFromLocation([...next, g], location, src.days, src.classes, g.id);
      }
    }
    return { next, added, replaced };
  };
  const dupPlan = planDup();

  const classOptions = (catalogQ.data?.class_names ?? []).map((n) => ({ value: n, label: n }));
  const locOptions = (catalogQ.data?.locations ?? []).map((l) => ({ value: l, label: l }));

  // "בחר הכל / נקה" header for the class multi-selects. onMouseDown preventDefault
  // keeps the dropdown open (no blur) so the click registers on the button.
  const allClassNames = catalogQ.data?.class_names ?? [];
  const classDropdown =
    (onPick: (v: string[]) => void) =>
    (menu: ReactElement): ReactElement => (
      <>
        <div className="flex items-center gap-3 px-3 py-1.5 border-b">
          <Button
            size="small"
            type="link"
            className="px-0"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onPick(allClassNames)}
            disabled={allClassNames.length === 0}
          >
            בחר הכל
          </Button>
          <Button
            size="small"
            type="link"
            className="px-0"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onPick([])}
          >
            נקה
          </Button>
        </div>
        {menu}
      </>
    );

  return (
    <div>
      <div className="text-small text-secondary-text mb-2">
        תוספת קבועה בש״ח לכל מפגש של שיעור מסוים בסניף מסוים שאיש הצוות מעביר. בחרו כמה שיעורים
        יחד, סניף וסכום. אפשר להגביל לימים מסוימים בשבוע — תוספת ליום מסוים גוברת על תוספת לכל
        הימים לאותו שיעור (למשל ₪20 לכל הימים ו־₪50 בשבת → בשבת ישולמו ₪50).
      </div>

      <div className="flex items-end gap-2 mb-3 flex-wrap">
        <Select
          mode="multiple"
          showSearch
          allowClear
          placeholder="שיעורים"
          style={{ minWidth: 240, flex: 1 }}
          value={classes}
          onChange={setClasses}
          options={classOptions}
          optionFilterProp="label"
          loading={catalogQ.isLoading}
          dropdownRender={classDropdown(setClasses)}
        />
        <Select
          showSearch
          placeholder="סניף"
          style={{ minWidth: 140 }}
          value={loc}
          onChange={setLoc}
          options={locOptions}
          optionFilterProp="label"
          loading={catalogQ.isLoading}
        />
        <Select
          mode="multiple"
          allowClear
          placeholder="כל הימים"
          style={{ minWidth: 140 }}
          value={days}
          onChange={setDays}
          options={DAY_OPTIONS}
          optionFilterProp="label"
        />
        <InputNumber
          min={0}
          prefix="₪"
          placeholder="למפגש"
          value={amt || undefined}
          onChange={(v) => setAmt(Number(v) || 0)}
          dir="ltr"
          style={{ width: 120 }}
        />
        <Button type="primary" disabled={!canAdd} onClick={add}>
          הוסף
        </Button>
      </div>

      {groups.length > 0 && (
        <div className="border rounded">
          {groups.map((g) => (
            <div key={g.id} className="px-3 py-3 border-b last:border-b-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="text-small text-secondary-text">סניף</span>
                <Select
                  showSearch
                  placeholder="סניף"
                  style={{ minWidth: 140 }}
                  value={g.location || undefined}
                  onChange={(v) => patchGroup(g.id, { location: v })}
                  options={locOptions}
                  optionFilterProp="label"
                />
                <span className="text-small text-secondary-text ms-2">ימים</span>
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="כל הימים"
                  style={{ minWidth: 140 }}
                  value={g.days}
                  onChange={(v) => patchGroup(g.id, { days: v })}
                  options={DAY_OPTIONS}
                  optionFilterProp="label"
                />
                <span className="text-small text-secondary-text ms-2">תוספת</span>
                <InputNumber
                  min={0}
                  prefix="₪"
                  value={g.amount || undefined}
                  onChange={(v) => patchGroup(g.id, { amount: Number(v) || 0 })}
                  dir="ltr"
                  style={{ width: 120 }}
                />
                <span className="text-small text-secondary-text">למפגש</span>
                <span className="flex-1" />
                <Button
                  size="small"
                  type="text"
                  disabled={!g.location || g.amount <= 0}
                  onClick={() => setDup({ id: g.id, locs: [] })}
                >
                  שכפול לסניפים
                </Button>
                <Button size="small" danger type="text" onClick={() => removeGroup(g.id)}>
                  הסר קבוצה
                </Button>
              </div>
              <Select
                mode="multiple"
                showSearch
                allowClear
                placeholder="שיעורים"
                style={{ width: '100%' }}
                value={g.classes}
                onChange={(v) => setGroupClasses(g.id, v)}
                options={classOptions}
                optionFilterProp="label"
                loading={catalogQ.isLoading}
                dropdownRender={classDropdown((v) => setGroupClasses(g.id, v))}
              />
              {dup?.id === g.id && (
                <DuplicatePanel
                  added={dupPlan.added}
                  replaced={dupPlan.replaced}
                  onApply={() => {
                    commit(dupPlan.next);
                    setDup(null);
                  }}
                  onCancel={() => setDup(null)}
                >
                  <span className="text-small text-secondary-text">לסניפים</span>
                  <Select
                    mode="multiple"
                    showSearch
                    placeholder="בחרו סניפים"
                    style={{ minWidth: 220, flex: 1 }}
                    value={dup.locs}
                    onChange={(locs) => setDup({ ...dup, locs })}
                    options={locOptions.filter((o) => o.value !== g.location)}
                    optionFilterProp="label"
                  />
                </DuplicatePanel>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
